// Enumerations
import { AppErrorEnumeration } from 'enumerations/app-error.enumeration';
// Types
import { ErrorType } from 'providers/error/types/error.type';

const isControlledError = (error: ErrorType['error']): boolean =>
  Boolean(error && Object.values(AppErrorEnumeration).includes(error as AppErrorEnumeration)) ||
  (typeof error === 'string' && error?.startsWith(AppErrorEnumeration.VERIDAS_ERROR_EVENT)) ||
  (typeof error === 'string' &&
    error?.startsWith(AppErrorEnumeration.VERIDAS_VIDEOCALL_ERROR_EVENT));

const convertErrorToErrorEnum = (error: ErrorType['error']): AppErrorEnumeration =>
  isControlledError(error) ? (error as AppErrorEnumeration) : AppErrorEnumeration.UNKNOWN_ERROR;

const onboardingTimeLimeHasReached = (error: ErrorType['error']): boolean =>
  error === AppErrorEnumeration.ONBOARDING_TIME_LIMIT_REACHED;

const isContractError = (error: ErrorType['error']): boolean =>
  [
    AppErrorEnumeration.SAVE_PAYMENT_METHOD_FAILED,
    AppErrorEnumeration.SAVE_CONTRACTUAL_FAILED,
    AppErrorEnumeration.GENERATE_OTP_FAILED,
    AppErrorEnumeration.CONTRACTUAL_SCREEN_DETAILS_FAILED,
    AppErrorEnumeration.VERIFY_OTP_FAILED,
    AppErrorEnumeration.GET_OTP_INFO_FAILED,
    AppErrorEnumeration.SAVE_PRECONTRACT_FAILED_WITH_CONSENTS,
    AppErrorEnumeration.PRECONTRACT_FAILED_NO_CONSENTS,
    AppErrorEnumeration.PRECONTRACT_SCREEN_DETAILS_FAILED,
    AppErrorEnumeration.GET_SIGNATURE_DETAILS_FAILED,
  ].includes(error as AppErrorEnumeration);

const isKycError = (error: ErrorType['error']): boolean =>
  [
    AppErrorEnumeration.BIZUM_WS_STATUS_REJECTED,
    AppErrorEnumeration.BIZUM_WS_ERROR,
    AppErrorEnumeration.KYC_INITIALIZE_FAILED,
    AppErrorEnumeration.KYC_METHOD_WS_STATUS_REJECTED,
    AppErrorEnumeration.COMPLETE_ACCOUNT_CHECK_FAILED,
    AppErrorEnumeration.ACCOUNT_CHECK_INVALID_TINK_ERROR,
    AppErrorEnumeration.WS_BIZUM_TIMEOUT,
    AppErrorEnumeration.KYC_BIZUM_WS_STATUS_REJECTED,
    AppErrorEnumeration.WS_VIDEOCALL_TIMEOUT,
    AppErrorEnumeration.VIDEOCALL_IFRAME_LOAD_ERROR,
    AppErrorEnumeration.ACCOUNT_CHECK_IFRAME_LOAD_ERROR,
    AppErrorEnumeration.VIDEOCALL_IFRAME_FETCH_URL_ERROR,
    AppErrorEnumeration.ACCOUNT_CHECK_IFRAME_FETCH_URL_ERROR,
    AppErrorEnumeration.GET_IDENTIFICATION_DETAILS_FAILED,
    AppErrorEnumeration.BACK_STATE_KYC_FAILED,
    AppErrorEnumeration.VERIDAS_VIDEOCALL_ERROR_EVENT,
    AppErrorEnumeration.KYC_BIZUM_POLLING_ERROR,
    AppErrorEnumeration.KYC_METHOD_POLLING_ERROR,
    AppErrorEnumeration.KYC_BIZUM_TIMER_EXPIRED,
  ].includes(error as AppErrorEnumeration) ||
  (typeof error === 'string' &&
    error.startsWith(AppErrorEnumeration.VERIDAS_VIDEOCALL_ERROR_EVENT));

const isPaymentError = (error: ErrorType['error']): boolean =>
  [
    AppErrorEnumeration.STRIPE_SUBMIT_ELEMENTS_FAILED,
    AppErrorEnumeration.STRIPE_PAYMENT_ELEMENT_LOAD_ERROR,
    AppErrorEnumeration.STRIPE_CREATE_PAYMENT_METHOD_FAILED,
    AppErrorEnumeration.STRIPE_PAYMENT_METHOD_WITHOUT_CARD,
    AppErrorEnumeration.STRIPE_CHECK_PAYMENT_CARD_FAILED,
    AppErrorEnumeration.STRIPE_CONFIRM_PAYMENT_FAILED,
    AppErrorEnumeration.PAYMENT_INITIALIZE_FAILED,
    AppErrorEnumeration.PAYMENT_LOAD_STRIPE_SCRIPT_ERROR,
    AppErrorEnumeration.GET_PAYMENT_DETAILS_FAILED,
    AppErrorEnumeration.WS_PAYMENT_TIMEOUT,
    AppErrorEnumeration.PAYMENT_CARD_ERROR,
  ].includes(error as AppErrorEnumeration);

const isProofIncomeError = (error: ErrorType['error']): boolean =>
  [
    AppErrorEnumeration.PROOF_OF_INCOME_WS_REJECTED,
    AppErrorEnumeration.COMPLETE_OPENBANKING_FAILED,
    AppErrorEnumeration.OPENBANKING_INVALID_TINK_ERROR,
    AppErrorEnumeration.WS_TGSS_TIMEOUT,
    AppErrorEnumeration.INITIALIZE_PROOF_OF_INCOME_FAILED,
    AppErrorEnumeration.TGSS_IFRAME_LOAD_ERROR,
    AppErrorEnumeration.OPENBANKING_IFRAME_LOAD_ERROR,
    AppErrorEnumeration.TGSS_IFRAME_FETCH_URL_ERROR,
    AppErrorEnumeration.OPENBANKING_IFRAME_FETCH_URL_ERROR,
    AppErrorEnumeration.GET_INCOME_DETAILS_FAILED,
    AppErrorEnumeration.BACK_STATE_PROOF_OF_INCOME_FAILED,
  ].includes(error as AppErrorEnumeration);

const isUploadIdError = (error: ErrorType['error']): boolean =>
  [
    AppErrorEnumeration.SAVE_DIFFERENT_DATA_FAILED,
    AppErrorEnumeration.FETCH_OCR_DATA_FAILED,
    AppErrorEnumeration.NO_VALIDATION_ID_ERROR,
    AppErrorEnumeration.EXPRESS_ID_URL_GENERATION_FAILED,
    AppErrorEnumeration.UPLOAD_ID_VERIFICATION_WS_REJECTED,
    AppErrorEnumeration.VERIDAS_ERROR_EVENT,
    AppErrorEnumeration.VERIDAS_CAMERA_FAILURE_EVENT,
    AppErrorEnumeration.WS_VERIDAS_TIMEOUT,
    AppErrorEnumeration.UPLOAD_ID_IFRAME_FETCH_URL_ERROR,
    AppErrorEnumeration.UPLOAD_ID_IFRAME_LOAD_ERROR,
    AppErrorEnumeration.UPLOAD_ID_ERROR_NO_MAPPED,
    AppErrorEnumeration.GET_ID_VALIDATION_DETAILS_FAILED,
    AppErrorEnumeration.UPLOAD_ID_POLLING_WRONG_STATE,
    AppErrorEnumeration.GET_VERIFICATION_OPTIONS_FAILED,
    AppErrorEnumeration.UPLOAD_ID_CREATE_VALIDATION_ID_FAILED,
    AppErrorEnumeration.UPLOAD_DOCUMENT_VERIFICATION_REJECTED,
    AppErrorEnumeration.UPLOAD_DOCUMENT_CONTINUE_ERROR,
    AppErrorEnumeration.UPLOAD_ID_SELFIE_REQUEST_URI_ERROR,
    AppErrorEnumeration.UPLOAD_ID_SELFIE_UPLOAD_FILE_ERROR,
    AppErrorEnumeration.UPLOAD_ID_SELFIE_UPLOAD_SELFIE_ERROR,
    AppErrorEnumeration.UPLOAD_ID_SELFIE_SDK_ERROR_EVENT,
    AppErrorEnumeration.UPLOAD_ID_SELFIE_CONSENT_ERROR,
    AppErrorEnumeration.UPLOAD_ID_BIZUM_TIMER_EXPIRED,
    AppErrorEnumeration.UPLOAD_ID_BIZUM_BACK_STATE_FAILED,
    AppErrorEnumeration.UPLOAD_ID_BIZUM_BUSINESS_PROCESS_FAILED,
    AppErrorEnumeration.UPLOAD_ID_BIZUM_POLLING_ERROR,
    AppErrorEnumeration.BIZUM_VERIFICATION_URL_GENERATION_FAILED,
    AppErrorEnumeration.UPLOAD_ID_BIZUM_REJECTED,
    AppErrorEnumeration.WS_TIME_MAX_ALIVE_REACHED,
  ].includes(error as AppErrorEnumeration) ||
  (typeof error === 'string' && error.startsWith(AppErrorEnumeration.VERIDAS_ERROR_EVENT));

const isOnboardingFlowError = (error: ErrorType['error']): boolean =>
  isContractError(error) ||
  isKycError(error) ||
  isProofIncomeError(error) ||
  isPaymentError(error) ||
  isUploadIdError(error);

const isUploadIdCameraFailureError = (error: ErrorType['error']): boolean =>
  error === AppErrorEnumeration.VERIDAS_CAMERA_FAILURE_EVENT ||
  error === AppErrorEnumeration.UPLOAD_ID_SELFIE_SDK_ERROR_EVENT;

const isUploadIdBizumError = (error: ErrorType['error']): boolean =>
  error === AppErrorEnumeration.UPLOAD_ID_BIZUM_BUSINESS_PROCESS_FAILED;

export {
  convertErrorToErrorEnum,
  onboardingTimeLimeHasReached,
  isContractError,
  isKycError,
  isOnboardingFlowError,
  isPaymentError,
  isProofIncomeError,
  isUploadIdError,
  isUploadIdCameraFailureError,
  isUploadIdBizumError,
};
